import { useApp } from "@/App";
import type { Item } from "@/lib/types";
import { CATEGORY_LABELS, CONDITION_LABELS } from "@/lib/types";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
	Cpu, Shirt, BookOpen, Sofa, Dumbbell, Gamepad2, Music,
	Palette, Wrench, ChefHat, Trees, Gem, Package,
} from "lucide-react";

const iconMap: Record<string, React.FC<{ className?: string }>> = {
	Cpu, Shirt, BookOpen, Sofa, Dumbbell, Gamepad2, Music,
	Palette, Wrench, ChefHat, Trees, Gem, Package,
};

import { CATEGORY_ICONS } from "@/lib/types";

const conditionColors: Record<string, string> = {
	new: "bg-emerald-100 text-emerald-800",
	"like-new": "bg-green-100 text-green-800",
	good: "bg-blue-100 text-blue-800",
	fair: "bg-amber-100 text-amber-800",
	worn: "bg-orange-100 text-orange-800",
};

export function ItemCard({ item }: { item: Item }) {
	const { navigate } = useApp();
	const IconComponent = iconMap[CATEGORY_ICONS[item.category]] || Package;

	return (
		<Card
			className="group cursor-pointer transition-all hover:shadow-md hover:-translate-y-0.5 overflow-hidden"
			onClick={() => navigate(`/item/${item.id}`)}
		>
			<div className="aspect-[4/3] bg-muted flex items-center justify-center relative overflow-hidden">
				<div className="flex flex-col items-center gap-2 text-muted-foreground/50">
					<IconComponent className="h-12 w-12" />
					<span className="text-xs font-medium">{CATEGORY_LABELS[item.category]}</span>
				</div>
				<div className="absolute top-2 right-2">
					<Badge variant="secondary" className={`text-[10px] font-medium ${conditionColors[item.condition] || ""}`}>
						{CONDITION_LABELS[item.condition]}
					</Badge>
				</div>
			</div>
			<CardContent className="p-4">
				<h3 className="font-semibold text-sm leading-tight line-clamp-2 group-hover:text-primary transition-colors">
					{item.title}
				</h3>
				<p className="text-xs text-muted-foreground mt-1 line-clamp-2">
					{item.description}
				</p>
				<div className="flex items-center justify-between mt-3">
					<span className="text-xs text-muted-foreground">
						@{item.ownerUsername}
					</span>
					<span className="text-sm font-semibold text-primary">
						~${item.estimatedValue}
					</span>
				</div>
				{item.tags.length > 0 && (
					<div className="flex flex-wrap gap-1 mt-2">
						{item.tags.slice(0, 3).map((tag) => (
							<Badge key={tag} variant="outline" className="text-[10px] px-1.5 py-0">
								{tag}
							</Badge>
						))}
					</div>
				)}
			</CardContent>
		</Card>
	);
}
